import { Fragment } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { BestTimeResponse, CampaignSummary } from '@crosscastr/shared';
import { api } from '../lib/api.js';
import { fmt, relTime } from '../lib/format.js';
import { Icon } from '../components/Icon.js';
import { EmptyState } from '../components/EmptyState.js';
import { TableSkeleton } from '../components/Skeleton.js';

// "דוחות" — one page, three in-page tabs (שידורים · לחיצות · היסטוריה). Merges the former
// /campaigns, /links and /activity so everything about "what happened" lives in one calm place.
// The old URLs redirect here (see App.tsx). Drill-in per broadcast stays at /campaigns/:id.

type Tab = 'broadcasts' | 'clicks' | 'history';
const TABS: { key: Tab; label: string }[] = [
  { key: 'broadcasts', label: 'שידורים' },
  { key: 'clicks', label: 'לחיצות' },
  { key: 'history', label: 'היסטוריה' },
];
const SUB: Record<Tab, string> = {
  broadcasts: 'מה קרה עם כל הודעה — כמה נשלח, הגיע ונקרא',
  clicks: 'כל קישור מתקצר ונמדד — כמה לחצו, לפי הודעה, קבוצה ומספר',
  history: 'כל מה שקרה במערכת — לפי זמן, פעולה ותוצאה',
};

export function ReportsPage(): JSX.Element {
  const navigate = useNavigate();
  const { tab: raw } = useParams();
  const tab: Tab = raw === 'clicks' || raw === 'history' ? raw : 'broadcasts';

  return (
    <div className="content">
      <div className="page-head">
        <div>
          <div className="eyebrow">תובנות</div>
          <h1>דוחות</h1>
          <div className="sub">{SUB[tab]}</div>
        </div>
        <button className="btn" onClick={() => navigate('/composer')}><Icon name="send" /> הודעה חדשה</button>
      </div>

      <div className="filter-tabs" style={{ marginBottom: 20 }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            className={tab === t.key ? 'on' : ''}
            onClick={() => navigate(t.key === 'broadcasts' ? '/reports' : `/reports/${t.key}`)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'broadcasts' && <BroadcastsTab />}
      {tab === 'clicks' && <ClicksTab />}
      {tab === 'history' && <HistoryTab />}
    </div>
  );
}

const STATUS_CHIP: Record<CampaignSummary['status'], { cls: string; label: string }> = {
  draft: { cls: 'muted', label: 'טיוטה' },
  scheduled: { cls: 'teal', label: 'מתוזמן' },
  running: { cls: 'gold', label: 'פעיל' },
  paused: { cls: 'gold', label: 'מושהה' },
  completed: { cls: 'green', label: 'הושלם' },
  failed: { cls: 'risk', label: 'נכשל' },
};
const TYPE_LABEL: Record<CampaignSummary['type'], string> = { one_time: 'חד-פעמי', scheduled: 'מתוזמן', recurring: 'חוזר' };

function BroadcastsTab(): JSX.Element {
  const navigate = useNavigate();
  const { data: campaigns, isLoading } = useQuery({ queryKey: ['campaigns'], queryFn: api.campaigns });

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      {isLoading || !campaigns ? (
        <TableSkeleton rows={4} />
      ) : campaigns.length === 0 ? (
        <EmptyState
          icon="analytics"
          title="עדיין אין הודעות"
          sub="ההודעה הראשונה שתשלח תופיע כאן — עם כמה נשלח, הגיע ונקרא, ושליחה חוזרת למי שלא קיבל."
          cta={<button className="btn" onClick={() => navigate('/composer')}><Icon name="send" /> כתיבת הודעה</button>}
        />
      ) : (
        <table className="dtable">
          <thead>
            <tr><th>הודעה</th><th>סוג</th><th>מצב</th><th>נשלחו</th><th>נכשלו</th><th>נוצר</th></tr>
          </thead>
          <tbody>
            {campaigns.map((c) => {
              const chip = STATUS_CHIP[c.status];
              return (
                <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/campaigns/${c.id}`)}>
                  <td style={{ fontWeight: 600 }}>{c.name}</td>
                  <td style={{ color: 'var(--text-dim)', fontSize: 13 }}>{TYPE_LABEL[c.type]}</td>
                  <td><span className={`chip ${chip.cls}`}>{chip.label}</span></td>
                  <td className="mono">{fmt(c.sentCount)}</td>
                  <td className="mono" style={{ color: c.failedCount ? 'var(--risk)' : 'var(--text-dim)' }}>{fmt(c.failedCount)}</td>
                  <td className="mono" style={{ color: 'var(--text-dim)', fontSize: 12.5 }}>{relTime(c.createdAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

const FAMILY_LABEL: Record<string, string> = { mobile: 'מובייל', desktop: 'דסקטופ', bot: 'בוטים/תצוגה מקדימה', other: 'אחר' };
const DOW_LABEL = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];

function ClicksTab(): JSX.Element {
  const { data, isLoading } = useQuery({ queryKey: ['links'], queryFn: () => api.links() });
  const { data: bestTime } = useQuery({ queryKey: ['bestTime'], queryFn: () => api.bestTime() });

  const maxDay = data ? Math.max(1, ...data.series.map((s) => s.clicks)) : 1;
  const maxFam = data ? Math.max(1, ...data.byFamily.map((f) => f.clicks)) : 1;

  if (isLoading || !data) {
    return <div className="card"><TableSkeleton rows={5} /></div>;
  }
  if (data.totalLinks === 0) {
    return (
      <div className="card">
        <EmptyState
          icon="eye"
          title="עדיין אין קישורים"
          sub="כששולחים הודעה עם קישור — הוא מתקצר אוטומטית וכל לחיצה נספרת כאן, לפי קבוצה ומספר."
        />
      </div>
    );
  }

  return (
    <>
      <div className="grid-2" style={{ marginBottom: 20 }}>
        <div className="card" style={{ padding: 22, display: 'flex', gap: 28 }}>
          <Stat label="קישורים פעילים" value={fmt(data.totalLinks)} />
          <Stat label="סה״כ לחיצות" value={fmt(data.totalClicks)} accent />
        </div>
        <div className="card" style={{ padding: 22 }}>
          <div className="card-h" style={{ padding: 0, marginBottom: 14 }}><h3>לפי סוג מכשיר</h3></div>
          {data.byFamily.length === 0 ? (
            <div style={{ color: 'var(--text-dim)', fontSize: 14 }}>עדיין אין לחיצות בטווח.</div>
          ) : (
            data.byFamily.map((f) => (
              <div key={f.family} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <span style={{ width: 130, fontSize: 13, color: 'var(--text-dim)' }}>{FAMILY_LABEL[f.family] ?? f.family}</span>
                <span style={{ flex: 1, height: 8, background: 'var(--surface-1)', borderRadius: 999, overflow: 'hidden' }}>
                  <span style={{ display: 'block', height: '100%', width: `${(f.clicks / maxFam) * 100}%`, background: 'var(--accent-teal)', borderRadius: 999 }} />
                </span>
                <span className="mono" style={{ width: 44, textAlign: 'start' }}>{fmt(f.clicks)}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {data.series.length > 0 && (
        <div className="card" style={{ padding: 22, marginBottom: 20 }}>
          <div className="card-h" style={{ padding: 0, marginBottom: 16 }}><h3>לחיצות לאורך זמן</h3><span className="meta">30 ימים אחרונים</span></div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 120 }}>
            {data.series.map((s) => (
              <div key={s.date} title={`${s.date}: ${s.clicks}`} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <span style={{ width: '100%', maxWidth: 22, height: `${(s.clicks / maxDay) * 96}px`, minHeight: 3, background: 'var(--accent-teal)', borderRadius: 'var(--r-sm)' }} />
                <span className="ltr" style={{ fontSize: 10, color: 'var(--text-dim)' }}>{s.date.slice(5)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {bestTime && bestTime.totalEvents > 0 && (
        <div className="card" style={{ padding: 22, marginBottom: 20 }}>
          <div className="card-h" style={{ padding: 0, marginBottom: 16 }}>
            <h3>שעות שיא לשליחה</h3>
            {bestTime.best && <span className="meta">מומלץ: {bestTime.best.label}</span>}
          </div>
          <BestTimeHeatmap data={bestTime} />
        </div>
      )}

      <div className="section-title"><h2 style={{ fontSize: 20 }}>קישורים</h2><span className="rule" /></div>
      <div className="card" style={{ overflow: 'hidden' }}>
        <table className="dtable">
          <thead><tr><th>קוד</th><th>כתובת מקורית</th><th>הודעה</th><th>קבוצה</th><th>לחיצות</th></tr></thead>
          <tbody>
            {data.links.map((l) => (
              <tr key={l.id}>
                <td className="mono ltr">{l.code}</td>
                <td className="ltr" style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-dim)', fontSize: 13 }}>{l.originalUrl}</td>
                <td>{l.campaignName ?? '—'}</td>
                <td>{l.targetName ?? '—'}</td>
                <td className="mono" style={{ fontWeight: 600 }}>{fmt(l.clicks)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function HistoryTab(): JSX.Element {
  const { data: activity, isLoading } = useQuery({ queryKey: ['activity'], queryFn: () => api.activity(80) });
  const BADGE_LABEL: Record<string, string> = { ok: 'הושלם', warm: 'שים לב', neutral: 'מתוזמן', risk: 'כשל' };

  return (
    <div className="card activity">
      {isLoading || !activity ? (
        <TableSkeleton rows={5} />
      ) : activity.length === 0 ? (
        <EmptyState
          icon="activity"
          title="אין עדיין היסטוריה"
          sub="כל פעולה — שליחת הודעה, חיבור מספר, שינוי קבוצה — תירשם כאן עם הזמן והתוצאה."
        />
      ) : (
        activity.map((e) => (
          <div key={e.id} className="act-row">
            <span className="act-time ltr">{relTime(e.at)}</span>
            <span className="act-txt">{e.message}</span>
            <span className={`act-badge ${e.status}`}>{BADGE_LABEL[e.status] ?? e.status}</span>
          </div>
        ))
      )}
    </div>
  );
}

function BestTimeHeatmap({ data }: { data: BestTimeResponse }): JSX.Element {
  const max = Math.max(1, ...data.heatmap.flat());
  const best = data.best;
  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: `28px repeat(24, 1fr)`, gap: 3, minWidth: 560 }}>
        <span />
        {Array.from({ length: 24 }, (_, h) => (
          <span key={h} className="ltr" style={{ fontSize: 9, color: 'var(--text-dim)', textAlign: 'center' }}>
            {h % 3 === 0 ? h : ''}
          </span>
        ))}
        {data.heatmap.map((row, d) => (
          <Fragment key={d}>
            <span style={{ fontSize: 11, color: 'var(--text-dim)', display: 'flex', alignItems: 'center' }}>{DOW_LABEL[d]}</span>
            {row.map((count, h) => {
              const isBest = best?.dow === d && best?.hour === h;
              const alpha = count === 0 ? 0.06 : 0.18 + (count / max) * 0.82;
              return (
                <span
                  key={`${d}-${h}`}
                  title={`יום ${DOW_LABEL[d]} · ${String(h).padStart(2, '0')}:00 · ${count}`}
                  style={{
                    height: 16,
                    borderRadius: 3,
                    background: `color-mix(in srgb, var(--accent-teal) ${Math.round(alpha * 100)}%, transparent)`,
                    outline: isBest ? '2px solid var(--accent-teal)' : 'none',
                  }}
                />
              );
            })}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }): JSX.Element {
  return (
    <div>
      <div style={{ fontSize: 13, color: 'var(--text-dim)', marginBottom: 6 }}>{label}</div>
      <div className="mono" style={{ fontSize: 30, fontWeight: 700, color: accent ? 'var(--accent-teal)' : 'var(--text)' }}>{value}</div>
    </div>
  );
}
